import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import { IObjectCharTypesSearch, IObjectCharTypes } from "../../types/ICharacteristicsSearch";
import {
  getCharacteristicDicRoute,
  searchObjectCharTypesRoute,
  searchObjectTypesRoute,
} from "./characteristicsRoutes";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import rtkRequestStatusCodes from "../../../../lib/const/rtkRequestStatusCodes";
import { DictionaryDto, ObjectType } from "../../../../lib/interfaces";

interface InitialState {
  objectCharTypes: IObjectCharTypes<string>[];
  objectCharTypesLoading: LoadingStatusCode;
  objectCharTypesFromDate: string | undefined;
  characteristicDictionariesLoading: LoadingStatusCode;
  characteristicDictionaries: { dicType?: string; data: DictionaryDto[] }[];
  objectTypes: ObjectType[];
  objectTypesLoading: LoadingStatusCode;
  isUpdateParams: { isDisabled: boolean; isClearTable: boolean };
  prevFromDate: string | undefined;
  characteristicsUnitsDicLoading: LoadingStatusCode;
  characteristicsUnitsDic: DictionaryDto[];
}

const initialState: InitialState = {
  objectCharTypes: [],
  objectCharTypesLoading: rtkRequestStatusCodes.idle,
  objectCharTypesFromDate: undefined,
  characteristicDictionariesLoading: rtkRequestStatusCodes.idle,
  characteristicDictionaries: [],
  objectTypes: [],
  objectTypesLoading: rtkRequestStatusCodes.idle,
  isUpdateParams: { isDisabled: false, isClearTable: false },
  prevFromDate: undefined,
  characteristicsUnitsDicLoading: rtkRequestStatusCodes.idle,
  characteristicsUnitsDic: [],
};

// Получаем список характеристик объекта
export const fetchObjectCharTypes = createAsyncThunk<
  IObjectCharTypes<string>[],
  IObjectCharTypesSearch<string>
>("characteristics/fetchObjectCharTypes", async (params) => {
  try {
    const response = axios.get<IObjectCharTypes<string>[]>(searchObjectCharTypesRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки списка доступных характеристик для объекта. Попробуйте позже",
    });
  }
});

// Получение справочников
export const fetchCharacteristicDic = createAsyncThunk<
  { dicType?: string; data: DictionaryDto[] }[],
  string[]
>("offeringGroups/fetchCharacteristicDic", async (dicTypeArr) => {
  try {
    const request = Promise.all(
      dicTypeArr.map((dicType) => axios.get<DictionaryDto[]>(getCharacteristicDicRoute(dicType)))
    );

    const res = await request;

    // console.log({ res });

    const result = res?.map((item) => {
      const responseUrlParse = item.config.url?.split("/");

      const dicType = responseUrlParse?.[responseUrlParse?.length - 1];

      return { data: item.data, dicType };
    });

    return result;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки справочников. Попробуйте позже",
    });
  }
});

// Получаем список всех типов объектов
export const fetchObjectTypes = createAsyncThunk<ObjectType[]>(
  "characteristics/fetchObjectTypes",
  async () => {
    try {
      const response = axios.get<ObjectType[]>(searchObjectTypesRoute());

      const { data } = await response;

      // console.log({ data });

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка всех типов объектов. Попробуйте позже",
      });
    }
  }
);

// Получение справочника единиц измерения
export const fetchCharacteristicsUnitsDic = createAsyncThunk(
  "characteristics/fetchCharacteristicsUnitsDic",
  async () => {
    try {
      const response = axios.get(getCharacteristicDicRoute("price_unit"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка единиц измерения. Попробуйте позже",
      });
    }
  }
);

export const characteristicsSlice = createSlice({
  name: "characteristics",
  initialState,
  reducers: {
    setIsUpdateParams: (state, action) => {
      state.isUpdateParams = { ...state.isUpdateParams, ...action.payload };
    },
    setPrevFromDate: (state, action) => {
      // Сохраняем текущее значение как предыдущее
      state.prevFromDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchObjectCharTypes.pending, (state) => {
        state.objectCharTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchObjectCharTypes.rejected, (state) => {
        state.objectCharTypesLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchObjectCharTypes.fulfilled, (state, action) => {
        state.objectCharTypesLoading = rtkRequestStatusCodes.fulfilled;
        state.objectCharTypesFromDate = action.meta.arg.onDate;
        state.objectCharTypes = action.payload;
      })
      .addCase(fetchCharacteristicDic.pending, (state) => {
        state.characteristicDictionariesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchCharacteristicDic.rejected, (state) => {
        state.characteristicDictionariesLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchCharacteristicDic.fulfilled, (state, { payload }) => {
        state.characteristicDictionariesLoading = rtkRequestStatusCodes.fulfilled;
        state.characteristicDictionaries = payload;
      })
      .addCase(fetchObjectTypes.pending, (state) => {
        state.objectTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchObjectTypes.rejected, (state) => {
        state.objectTypesLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchObjectTypes.fulfilled, (state, { payload }) => {
        state.objectTypes = payload;
        state.objectTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchCharacteristicsUnitsDic.pending, (state) => {
        state.characteristicsUnitsDicLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchCharacteristicsUnitsDic.rejected, (state) => {
        state.characteristicsUnitsDicLoading = rtkRequestStatusCodes.rejected;
      })
      .addCase(fetchCharacteristicsUnitsDic.fulfilled, (state, { payload }) => {
        state.characteristicsUnitsDic = payload;
        state.characteristicsUnitsDicLoading = rtkRequestStatusCodes.fulfilled;
      });
  },
});

export const {
  reducer: characteristicsReducer,
  actions: characteristicsActions,
} = characteristicsSlice;
